import { ReactElement, useEffect, useRef, useState } from "react"
import { DefaultValues, FieldValues, UseFormReturn } from "react-hook-form-mui"
import { Form, IFormProps, SnackbarAlert } from ".."
import {
  LinearProgress,
  Button,
  CircularProgress,
  ButtonProps,
} from "@mui/material"
import {
  TypedUseQueryHookResult,
  UseMutation,
  UseQuery,
} from "@reduxjs/toolkit/dist/query/react/buildHooks"
import { MutationDefinition, QueryDefinition } from "@reduxjs/toolkit/query"
import { getNotEmptyObjectOrUndefined } from "@/core/utils"
import { MaybePromise } from "@reduxjs/toolkit/dist/query/tsHelpers"

export const SKIP_FORM_SUBMIT = Symbol("skip-form-submit")

export namespace RtkHF {
  export type FormAction = ButtonProps & {
    text: string
  }
  export interface RtkHookFormProps<
    F extends FieldValues,
    MDRT,
    SubmitHook extends UseMutation<MutationDefinition<any, any, any, MDRT>>,
    QDRT,
    InitHook extends UseQuery<QueryDefinition<any, any, any, QDRT>>,
  > extends Omit<IFormProps<F>, "actions" | "onSubmit"> {
    actions: FormAction[]
    mutationHook: SubmitHook
    mutationHookParams?: Parameters<SubmitHook>[0]
    getSubmitHandlerParams?: (
      body: F,
    ) => MaybePromise<
      | Partial<Parameters<ReturnType<SubmitHook>[0]>[0]>
      | typeof SKIP_FORM_SUBMIT
    >
    initialHook?: InitHook
    initialHookParams?: Parameters<InitHook>
    getDefaultValues?: (
      data: TypedUseQueryHookResult<QDRT, any, any>["data"],
    ) => DefaultValues<F>
    // getBodyValues?: (
    //   data: F,
    // ) => Parameters<ReturnType<SubmitHook>[0]>[0]["body"]
    /** Await completion before resetting; ignore any callback return value. */
    afterSubmit?: (res: any) => unknown
    resetOnSuccess?: boolean
    onSubmitError?: (
      error: unknown,
      context: UseFormReturn<F>,
    ) => void | Promise<void>
    /** Apply the first successful defaults only. Remount by resource ID. */
    initializeOnce?: boolean
    successMessage?: string
    setFormContext?: (ctx: UseFormReturn<F>) => void
  }

  export interface InitialFormState {
    isPreloading: boolean
    isPreloadingError: boolean
    isPreloadingSuccess: boolean
    isLoading: boolean
    isError: boolean
    isSuccess: boolean
    errorMessage: string | undefined
  }
}
/**
 * @todo как сделать, чтобы дженерики доставались из значений? я не знаю...
 */
export const RtkHookForm = <
  F extends FieldValues,
  MDRT = any,
  Submit extends UseMutation<
    MutationDefinition<any, any, any, MDRT>
  > = UseMutation<MutationDefinition<any, any, any, MDRT>>,
  QDRT = any,
  Init extends UseQuery<QueryDefinition<any, any, any, QDRT>> = UseQuery<
    QueryDefinition<any, any, any, QDRT>
  >,
>({
  initialHook: useInitialHook,
  initialHookParams,
  mutationHook: useMutationHook,
  mutationHookParams,
  getSubmitHandlerParams,
  actions,
  formContextParams = {},
  getDefaultValues,
  // getBodyValues,
  afterSubmit,
  resetOnSuccess = true,
  onSubmitError,
  initializeOnce = false,
  successMessage,
  setFormContext: setFormContextFromProps,
  ...props
}: RtkHF.RtkHookFormProps<F, MDRT, Submit, QDRT, Init>): ReactElement<
  RtkHF.RtkHookFormProps<F, MDRT, Submit, QDRT, Init>
> => {
  const [formContext, setFormContext] = useState<UseFormReturn<F>>()
  const [notification, setNotification] = useState<{
    variant: "error" | "success" | "warning"
    title: string
    message: string
  }>()
  const [isSnackbarOpen, setSnackbarOpen] = useState(false)
  const [hFormContextParams, setHFormContextParams] =
    useState(formContextParams)
  const [submitHandler, submitState] = useMutationHook(mutationHookParams)
  const initialData =
    useInitialHook && useInitialHook.apply(this, initialHookParams ?? [{}])

  const [isInitialized, setIsInitialized] = useState(!useInitialHook)
  const initializedDefaults = useRef(false)
  const initializedData = useRef<QDRT>()
  const data = initialData?.data
  const isInitialLoading = initialData?.isLoading
  const isInitialError = initialData?.isError
  const isInitialSuccess = initialData?.isSuccess
  const mounted = useRef(false)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (
      !isInitialSuccess ||
      data === undefined ||
      (initializedDefaults.current &&
        (initializeOnce || Object.is(initializedData.current, data)))
    ) {
      return
    }
    const defaultValues = getNotEmptyObjectOrUndefined({
      ...(getDefaultValues ? getDefaultValues(data) : data),
    })
    setHFormContextParams((current) => ({ ...current, defaultValues }))
    // A form already shown after an initial query error needs its first
    // successful defaults applied explicitly; RHF caches defaultValues.
    if (!initializedDefaults.current && isInitialized) {
      formContext?.reset(defaultValues)
    }
    initializedDefaults.current = true
    initializedData.current = data
  }, [
    data,
    isInitialSuccess,
    initializeOnce,
    getDefaultValues,
    formContext,
    isInitialized,
  ])

  useEffect(() => {
    // Preserve legacy uncached loading, but never unmount a retained draft
    // or an initialized form during a cached refetch.
    if (!isInitialLoading) {
      setIsInitialized(true)
    } else if (!initializeOnce && data === undefined) {
      setIsInitialized(false)
    }
  }, [isInitialLoading, initializeOnce, data])

  useEffect(() => {
    if (isInitialError) {
      setNotification({
        variant: "error",
        title: "Произошла ошибка",
        message: "Не удалось загрузить данные формы",
      })
      setSnackbarOpen(true)
    }
  }, [isInitialError])

  useEffect(() => {
    if (!formContext) {
      return
    }
    setFormContextFromProps && setFormContextFromProps(formContext)
  }, [formContext, setFormContextFromProps])

  if (!isInitialized) {
    return <LinearProgress />
  }

  return (
    <>
      <SnackbarAlert
        isOpen={isSnackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        variant={notification?.variant}
        title={notification?.title}
        message={notification?.message ?? ""}
      />
      <Form<F>
        setFormContext={setFormContext}
        formContextParams={hFormContextParams}
        onSubmit={async (body, _event, context = formContext) => {
          let saved = false
          setSnackbarOpen(false)
          try {
            const params = getSubmitHandlerParams
              ? await getSubmitHandlerParams(body)
              : { body }
            if (params === SKIP_FORM_SUBMIT) return

            const result = await submitHandler(params).unwrap()
            saved = true
            await afterSubmit?.(result)
            if (!mounted.current) return
            if (resetOnSuccess) context?.reset()
            setNotification({
              variant: "success",
              title: "Успешно",
              message: successMessage ?? "Успешно",
            })
            setSnackbarOpen(true)
          } catch (error) {
            if (!saved && onSubmitError && context) {
              try {
                await onSubmitError(error, context)
                return
              } catch {
                // A failed error handler must not escape Form's event handler.
              }
            }
            if (!mounted.current) return
            setNotification(
              saved
                ? {
                    variant: "warning",
                    title: "Изменения сохранены",
                    message:
                      "Изменения сохранены, но не удалось завершить обработку. Не отправляйте форму повторно; обновите данные.",
                  }
                : {
                    variant: "error",
                    title: "Произошла ошибка",
                    message: "Не удалось сохранить изменения",
                  },
            )
            setSnackbarOpen(true)
          }
        }}
        actions={actions.map(({ text, ...btnProps }) => (
          <Button disabled={submitState.isLoading} {...btnProps}>
            {text}&nbsp;
            {submitState.isLoading && <CircularProgress size={20} />}
          </Button>
        ))}
        {...props}
      />
    </>
  )
}
